/************************************************************************************

L-PRODUCT-INFO SECTION

*************************************************************************************/
if (!customElements.get('l-product-info')) {
	customElements.define(
		'l-product-info',
		class LProductInfo extends HTMLElement {
			quantityInput = undefined
			quantityForm = undefined
			onVariantChangeUnsubscriber = undefined
			cartUpdateUnsubscriber = undefined
			abortController = undefined
			pendingRequestUrl = null
			preProcessHtmlCallbacks = []
			postProcessHtmlCallbacks = []

			constructor() {
				super()
			}

			connectedCallback() {
				// Initialize the buttons state
				this.initializeProductSwapUtility()

				// Dispatch events
				this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }))

				// Catch variant picker options changed events
				this.onVariantChangeUnsubscriber = subscribe(
					PUB_SUB_EVENTS.optionValueSelectionChange,
					this.handleOptionValueChange.bind(this)
				)
			}

			disconnectedCallback() {
				this.onVariantChangeUnsubscriber()
				this.cartUpdateUnsubscriber?.()
			}

			addPreProcessCallback(callback) {
				this.preProcessHtmlCallbacks.push(callback)
			}

			initializeProductSwapUtility() {
				this.postProcessHtmlCallbacks.push((newNode) => {
					window?.Shopify?.PaymentButton?.init()
					window?.ProductModel?.loadShopifyXR()
				})
			}

			handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
				if (!this.contains(event.target)) return

				// Reset form data
				this.resetProductFormState()

				// Check new selected status
				const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url
				this.pendingRequestUrl = productUrl
				const shouldSwapProduct = this.dataset.url !== productUrl
				const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct

				// Render product information based on new selected options
				this.renderProductInfo({
					requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
					targetId: target.id,
					callback: shouldSwapProduct
						? this.handleSwapProduct(productUrl, shouldFetchFullPage)
						: this.handleUpdateProductInfo(productUrl),
				})
			}

			resetProductFormState() {
				const productForm = this.productForm
				productForm?.toggleSubmitButton(true, null, false)
				productForm?.handleErrorMessage()
			}

			renderProductInfo({ requestUrl, targetId, callback }) {
				this.abortController?.abort()
				this.abortController = new AbortController()

				fetch(requestUrl, { signal: this.abortController.signal })
					.then((response) => response.text())
					.then((responseText) => {
						this.pendingRequestUrl = null
						const html = new DOMParser().parseFromString(responseText, 'text/html')
						callback(html)
					})
					.then(() => {
						// set focus to last clicked option value
						document.querySelector(`#${targetId}`)?.focus()
					})
					.catch((error) => {
						if (error.name === 'AbortError') {
							console.log('Fetch aborted by user')
						} else {
							console.error(error)
						}
					})
			}

			handleSwapProduct(productUrl, updateFullPage) {
				return (html) => {
					const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info'
					const variant = this.getSelectedVariant(html.querySelector(selector))
					this.updateURL(productUrl, variant?.id)

					if (updateFullPage) {
						document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML

						HTMLUpdateUtility.viewTransition(
							document.querySelector('main'),
							html.querySelector('main'),
							this.preProcessHtmlCallbacks,
							this.postProcessHtmlCallbacks
						)
					} else {
						HTMLUpdateUtility.viewTransition(
							this,
							html.querySelector('product-info'),
							this.preProcessHtmlCallbacks,
							this.postProcessHtmlCallbacks
						)
					}
				}
			}

			handleUpdateProductInfo(productUrl) {
				return (html) => {
					const variant = this.getSelectedVariant(html)

					this.updateOptionValues(html)
					this.updateURL(productUrl, variant?.id)
					this.updateVariantInputs(variant?.id)

					if (!variant) {
						this.setUnavailable()

						return
					}

					const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
						const source = html.getElementById(`${id}-${this.sectionId}`)
						const destination = this.querySelector(`#${id}-${this.dataset.section}`)
						if (source && destination) {
							destination.innerHTML = source.innerHTML
							destination.classList.toggle('l-hidden', shouldHide(source))
						}
					}

					updateSourceFromDestination('price')
					updateSourceFromDestination('ProductSubmitButton')

					this.productForm?.toggleSubmitButton(
						html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
						window.themeVariables.strings.collectionSoldOut,
						false
					)

					publish(PUB_SUB_EVENTS.variantChange, {
						data: {
							sectionId: this.sectionId,
							html,
							variant,
						},
					})
				}
			}

			updateOptionValues(html) {
				const variantSelects = html.querySelector('l-variant-selects')

				if (variantSelects) {
					HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks)
				}
			}

			updateURL(url, variantId) {
				if (this.dataset.updateUrl === 'false') return

				window.history.replaceState(
					{},
					'',
					`${url}${variantId ? `${url.includes('?') ? '&' : '?'}variant=${variantId}` : ''}`
				)
			}

			updateVariantInputs(variantId) {
				this.querySelectorAll(
					`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
				).forEach((productForm) => {
					const input = productForm.querySelector('input[name="id"]')
					input.value = variantId ?? ''
					input.dispatchEvent(new Event('change', { bubbles: true }))
				})
			}

			setUnavailable() {
				this.productForm?.toggleSubmitButton(true, window.themeVariables.strings.productFormUnavailable)

				const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
					.map((id) => `#${id}-${this.dataset.section}`)
					.join(', ')

				document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('l-hidden'))
			}

			getSelectedVariant(productInfoNode) {
				const selectedVariant = productInfoNode.querySelector('l-variant-selects [data-selected-variant]')?.innerHTML
				return !!selectedVariant ? JSON.parse(selectedVariant) : null
			}

			buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
				const params = []

				!shouldFetchFullPage && params.push(`section_id=${this.sectionId}`)

				if (optionValues.length) {
					params.push(`option_values=${optionValues.join(',')}`)
				}

				return `${url}?${params.join('&')}`
			}

			get productForm() {
				return this.querySelector('l-product-form')
			}

			get variantSelectors() {
				return this.querySelector('l-variant-selects')
			}

			get sectionId() {
				return this.dataset.originalSection || this.dataset.section
			}
		}
	)
}

/************************************************************************************

PRODUCT INFORMATION COMPONENTS
+ l-variant-selects
+ l-product-controls
+ l-product-quantity-input
+ l-product-delivery-estimation

*************************************************************************************/
if (!customElements.get('l-product-form')) {
	customElements.define(
		'l-product-form',
		class LProductForm extends HTMLElement {
			constructor() {
				super()
			}

			async connectedCallback() {
				// Elements
				this.form = this.querySelector('form')
				this.cart = document.getElementById('CartDrawer')
				this.submitButton = this.querySelector('[type="submit"]')
				this.submitButtonText = this.submitButton.querySelector('[data-add-to-cart-label]')

				// Set data / attribute
				this.sectionId = this.dataset.sectionId
				this.hideErrors = this.dataset.hideErrors === 'true'

				// Setup event
				this.form.addEventListener('submit', this.onSubmitHandler.bind(this))
				document.addEventListener('l:cart:error', ({ detail }) => {
					if (detail.id == this.section) {
						this.handleErrorMessage(detail.description)
					}
				})
			}

			onSubmitHandler(evt) {
				evt.preventDefault()

				if (this.submitButton.getAttribute('aria-disabled') === 'true') return

				// Setup button loading state
				this.submitButton.setAttribute('aria-disabled', true)
				this.submitButton.classList.add('is-loading')

				// Setup adding product to cart
				const config = L_ATC.handleConfigWithFormEl(this.form, [this.sectionId])

				L_ATC.addToCart(config, this.sectionId)
					.catch((e) => {
						console.error(e)
					})
					.finally(() => {
						// Remove button loading state
						this.submitButton.classList.remove('is-loading')
						if (!this.error) this.submitButton.removeAttribute('aria-disabled')
					})
			}

			handleErrorMessage(errorMessage = false) {
				if (this.hideErrors) return

				if (!this.errorMessageWrapper) {
					this.errorMessageWrapper = document.getElementById(`ProductErrorMessages-${this.sectionId}`)
					this.errorMessage = this.errorMessageWrapper.querySelector('[data-error-message]')
				}

				if (!this.errorMessageWrapper) return

				this.errorMessageWrapper.classList.toggle('l-hidden', !errorMessage)
				if (errorMessage) this.errorMessage.textContent = errorMessage
			}

			// This is global function
			toggleSubmitButton(disable = true, text, needToUpdateText = true) {
				if (disable) {
					this.submitButton.setAttribute('disabled', 'disabled')
					if (text) this.submitButtonText.textContent = text

					return
				}

				this.submitButton.removeAttribute('disabled')

				if (needToUpdateText) {
					this.submitButtonText.textContent = window.themeVariables.strings.productFormAddToCart
				}
			}

			get variantIdInput() {
				return this.form.querySelector('[name=id]')
			}
		}
	)
}

if (!customElements.get('l-variant-selects')) {
	customElements.define(
		'l-variant-selects',
		class LTAVariantSelect extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				this.addEventListener('change', (event) => {
					const target = this.getInputForEventTarget(event.target)

					this.updateSelectionMetadata(event)

					publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
						data: {
							event,
							target,
							selectedOptionValues: this.selectedOptionValues,
						},
					})
				})
			}

			updateSelectionMetadata({ target }) {
				const { value, tagName } = target

				if (tagName === 'SELECT' && target.selectedOptions.length) {
					return
				}

				// Update selected option value
				if (target.hasAttribute('data-product-variant-input')) {
					const selectedSwatchValue = target.closest('[data-variant-options]').querySelector('[data-selected-value]')
					if (selectedSwatchValue) selectedSwatchValue.innerHTML = value
				}
			}

			getInputForEventTarget(target) {
				return target.tagName === 'SELECT' ? target.selectedOptions[0] : target
			}

			get selectedOptionValues() {
				return Array.from(
					this.querySelectorAll('[data-variant-select] option[selected], [data-product-variant-input]:checked')
				).map(({ dataset }) => dataset.optionValueId)
			}
		}
	)
}

if (!customElements.get('l-product-controls')) {
	customElements.define(
		'l-product-controls',
		class LProductInfo extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				this.quantityFormEl = this.querySelector('l-product-quantity-input')
				this.quantityInputEl = this.quantityFormEl?.querySelector('input')
				this.productForm = this.querySelector('l-product-form')
				this.sectionId = this.dataset.section

				if (!this.quantityInputEl || !this.quantityFormEl) return

				// Subscribe global event
				this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.updateQuantityRules.bind(this))

				// Setup validate quantity controls
				this.setQuantityBoundries()

				// Events
				this.handleQuantityLimitOnCartUpdatedEvent()
			}

			disconnectedCallback() {
				if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber()
			}

			setQuantityBoundries() {
				// Get data from l-product-quantity-input element
				const quantityDataset = this.quantityInputEl.dataset
				const data = {
					cartQuantity: quantityDataset.cartQuantity ? parseInt(quantityDataset.cartQuantity) : 0,
					min: quantityDataset.min ? parseInt(quantityDataset.min) : 1,
					max: quantityDataset.max ? parseInt(quantityDataset.max) : null,
					step: this.quantityInputEl.step ? parseInt(this.quantityInputEl.step) : 1,
				}

				// Get min-max quantity of selected variant based on variant in current cart
				let min = data.min
				const max = data.max === null ? data.max : data.max - data.cartQuantity
				if (max !== null) min = Math.min(min, max)
				if (data.cartQuantity >= data.min) min = Math.min(min, data.step)

				// Set values for quantity input element (min, max, value)
				this.quantityInputEl.min = min
				this.quantityInputEl.value = min
				max ? (this.quantityInputEl.max = max) : this.quantityInputEl.removeAttribute('max')

				// Publish quantity update
				publish(PUB_SUB_EVENTS.quantityUpdate, undefined)
			}

			updateQuantityRules({ data }) {
				const { html, sectionId } = data

				const quantityFormUpdated = html.getElementById(`Product-Form-Controls-${sectionId}`)
				const selectors = ['[data-product-quantity-input]', '[data-quantity-label]']

				for (let selector of selectors) {
					const current = this.querySelector(selector)
					const updated = quantityFormUpdated.querySelector(selector)
					if (!current || !updated) continue

					// Update product quantity input || other elements
					if (selector === '[data-product-quantity-input]') {
						const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step']
						for (let attribute of attributes) {
							const valueUpdated = updated.getAttribute(attribute)
							valueUpdated !== null ? current.setAttribute(attribute, valueUpdated) : current.removeAttribute(attribute)
						}
					} else {
						current.innerHTML = updated.innerHTML
					}
				}

				// Update and dispatch quantity changed event
				this.setQuantityBoundries()
			}

			handleQuantityLimitOnCartUpdatedEvent() {
				const hasQuantityLimit = this.hasAttribute('data-quantity-limit')

				if (!hasQuantityLimit) return

				document.addEventListener('cart:updated', ({ detail, target }) => {
					// Event from adding product to cart (PDP page, Product card page)
					if (detail.id) {
						const { id, response } = detail
						const newProductInfoElText = response.sections?.[this.sectionId]

						if (!newProductInfoElText) return

						const newControlEl = l_getSectionDOM(newProductInfoElText, `#Product-Form-Controls-${this.sectionId}`)

						HTMLUpdateUtility.viewTransition(this, newControlEl)

						return
					}

					// Event from cart drawer (Cart page) -> Fetch product info section
					fetch(this.dataset.fetchUrl)
						.then((response) => response.text())
						.then((newProductInfoElText) => {
							const newControlEl = l_getSectionDOM(newProductInfoElText, `#Product-Form-Controls-${this.sectionId}`)
							this.innerHTML = newControlEl.innerHTML
						})
						.catch((e) => console.error(e))
				})
			}

			// b2b__fetchQuantityRules() {
			// 	// Get current selected variant ID
			// 	const currentVariantId = this.productForm?.variantIdInput?.value
			// 	if (!currentVariantId) return

			// 	// Call API to update rules cart component
			// 	this.querySelector('[data-rules-cart]').classList.add('is-loading')

			// 	fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
			// 		.then((response) => response.text())
			// 		.then((responseText) => {
			// 			const html = new DOMParser().parseFromString(responseText, 'text/html')
			// 			this.updateQuantityRules(this.dataset.section, html)
			// 		})
			// 		.catch((e) => console.error(e))
			// 		.finally(() => this.querySelector('[data-rules-cart]').classList.remove('is-loading'))
			// }
		}
	)
}

if (!customElements.get('l-product-quantity-input')) {
	customElements.define(
		'l-product-quantity-input',
		class LTAProductQuantityInput extends HTMLElement {
			constructor() {
				super()
			}

			quantityUpdateUnsubscriber = undefined

			connectedCallback() {
				// Elements
				this.input = this.querySelector('input')

				// Setup events
				this.changeEvent = new Event('change', { bubbles: true })

				this.input.addEventListener('change', () => {
					this.validateQtyRules()
				})

				this.querySelectorAll('button').forEach((button) =>
					button.addEventListener('click', this.onButtonClick.bind(this))
				)

				// Subscribe global event
				document.addEventListener('l:quantity:update', this.validateQtyRules.bind(this))

				// Validate Quantity Rules
				this.validateQtyRules()
			}

			onButtonClick(event) {
				event.preventDefault()

				const previousValue = this.input.value

				if (event.target.name === 'plus') {
					// Reset value to minimum value || increase value based on step
					const needToResetMinimumValue =
						parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0

					needToResetMinimumValue ? (this.input.value = this.input.dataset.min) : this.input.stepUp()
				} else {
					this.input.stepDown()
				}

				// Dispatch event
				if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent)

				// Reset to minimum value
				if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
					this.input.value = parseInt(this.input.min)
				}
			}

			validateQtyRules() {
				const value = parseInt(this.input.value)

				if (this.input.dataset.max) {
					this.input.max = parseInt(this.input.dataset.max)
				}

				if (this.input.dataset.min) {
					this.input.min = parseInt(this.input.dataset.min)
				}

				if (this.input.dataset.max == '1') {
					this.input.value = 1
				}

				if (this.input.min) {
					const buttonMinus = this.querySelector("button[name='minus']")
					buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min))
				}

				if (this.input.max) {
					const max = parseInt(this.input.max)

					this.input.max = max
					const buttonPlus = this.querySelector("button[name='plus']")
					buttonPlus.classList.toggle('disabled', value >= max)
				}
			}
		}
	)
}

if (!customElements.get('l-product-group')) {
	window.LTAProductGroupData = []
	
	function backToCollectionUrl() {
		const referrer = document.referrer;

		if (
			referrer &&
			referrer.startsWith(window.location.origin) &&
			/\/collections\/[^/]+/.test(new URL(referrer).pathname)
		) {
			const wrappers = document.querySelectorAll('.history-back-btn');

			if (wrappers.length) {
				wrappers.forEach((wrapper) => {
					wrapper.style.display = 'block';

					wrapper.addEventListener('click', function () {
						window.history.back();
					});
				});
			}
		}
	}

	function initFloatingAtc() {
      var productWrapper = document.querySelector('.l-product-controls__product-form');
      var floatingBar = document.getElementById('l-product__addtocart-floating');
      var header = document.querySelector('#shopify-section-header');
      if (!productWrapper || !floatingBar) return;

      var headerHeight = header ? header.offsetHeight : 0;

      // Clean old observer
      if (floatingBar._observer) {
        floatingBar._observer.disconnect();
      }

      var observer = new IntersectionObserver(
        function (entries) {
          var entry = entries[0];

          if (!entry.isIntersecting) {
            floatingBar.classList.add('show');
          } else {
            floatingBar.classList.remove('show');
          }
        },
        {
          root: null,
          threshold: 0,

          // 👇 KEY: offset by header height
          rootMargin: "-" + headerHeight + "px 0px 0px 0px"
        }
      );

      observer.observe(productWrapper);
      floatingBar._observer = observer;

    }

	customElements.define(
		'l-product-group',
		class LProductGroup extends HTMLElement {
			constructor() {
				super()
			}

			preProcessHtmlCallbacks = []
			postProcessHtmlCallbacks = []


			setSelectedSwatch(productLinkEl) {
			  this.productLinkEls = this.querySelectorAll('[data-product-link]') // refresh list if DOM changed
			  this.productLinkEls.forEach((el) => el.classList.remove('is-selected'))
			  productLinkEl.classList.add('is-selected')
			}

			connectedCallback() {
				// Data
				this.sectionId = this.dataset.sectionId

				// Elements
				this.productSectionEl = document.getElementById(`product`)
				this.productInfoEl = document.getElementById('ProductInfo')
				this.productLinkEls = this.querySelectorAll('[data-product-link]')
				this.templateMediaEl = document.getElementById(`ProductGroupMedia-${this.sectionId}`)

				// Setup events
				this.productLinkEls.forEach((productLinkEl) => {
					productLinkEl.addEventListener('click', (event) => {
						event.preventDefault()

						const isMobile = window.matchMedia('(max-width: 768px)').matches

						isMobile && l_lockBodyScroll()

						this.productLinkEls.forEach((_productLinkEl) => {
							_productLinkEl.classList.toggle('is-selected', _productLinkEl === productLinkEl)
						})

						const productURL = `${productLinkEl.dataset.productLink}`.includes('?view')
							? `${productLinkEl.dataset.productLink}&section_id=${this.sectionId}`
							: `${productLinkEl.dataset.productLink}?section_id=${this.sectionId}`
						const productInfo = window.LTAProductGroupData.find((productInfo) => productInfo.url === productURL)?.html

						this.updateMedia(productLinkEl)

						/*productInfo
							? this.renderFromCache(productLinkEl, productInfo)
							: this.fetchToRender(productLinkEl, productURL)*/

						this.fetchToRender(productLinkEl, productURL)

						if (isMobile) {
							l_debounce(() => {
								l_unLockBodyScroll()
							}, 300)()
						}

						publish(PUB_SUB_EVENTS.productGroupChange, {
							data: {
								productId: productLinkEl.dataset.productId,
								productURL: productLinkEl.dataset.productLink,
							},
						})

						
					})
				})

				// New PDP2: automatically scroll to the selected item when the page loads
				// const lgroupWrapper = document.querySelector('.l-product-group-wrapper');
				// const isSelected = lgroupWrapper?.querySelector('.is-selected');
				// isSelected?.scrollIntoView({
				// 	behavior: 'instant',
				// 	block: 'nearest',
				// 	inline: 'center'
				// });
				const mediaQuery = window.matchMedia('(max-width: 989px)')
				const scrollToSelectedProduct = () => {
					if (!mediaQuery.matches) return
					const selectedProduct = this.querySelector('.l-product-group-wrapper .is-selected')
					selectedProduct?.scrollIntoView({
						behavior: 'instant',
						block: 'nearest',
						inline: 'center',
					})
				}
				scrollToSelectedProduct()
				mediaQuery.addEventListener('change', scrollToSelectedProduct)



			}

			updateMedia(productLinkEl) {
				const productId = productLinkEl.dataset.productId
				const productMediaEl = document.getElementById('LMainProductMedia')

				const mediaContent = document.createElement('div')
				mediaContent.appendChild(this.templateMediaEl.content.firstElementChild.cloneNode(true))
				const newMediaEl = mediaContent.querySelector(`#ProductGroupMedia-${productId} #LMainProductMedia`)

				if (newMediaEl) productMediaEl.replaceWith(newMediaEl)
			}

			fetchToRender(productLinkEl, productURL) {
				this.productSectionEl.parentElement.classList.add('is-loading')
				fetch(productURL)
					.then((response) => response.text())
					.then((responseText) => {
						const newProductSection = new DOMParser().parseFromString(responseText, 'text/html')
						const productInfo = newProductSection.getElementById('ProductInfo')
						const productSection = newProductSection.getElementById('product')

						this.productSectionEl.dataset.section = productSection.dataset.section
						this.productSectionEl.dataset.productId = productSection.dataset.productId
						this.productSectionEl.dataset.url = productSection.dataset.url

						this.productInfoEl.replaceWith(productInfo)
						// Keep the same position of the product info element
						this.productInfoEl = this.querySelector('#ProductInfo')
						l_blockPosition(this.productInfoEl)

						requestAnimationFrame(() => {
							initFloatingAtc();
							backToCollectionUrl();
						});

						// Update the product info element
						window.LTAProductGroupData = [...window.LTAProductGroupData, { url: productURL, html: productInfo }]

					})
					.catch((error) => {
						l_debounce(() => this.productSectionEl.parentElement.classList.remove('is-loading'), 100)()
					})
					.finally(() => {
						l_debounce(() => this.productSectionEl.parentElement.classList.remove('is-loading'), 100)()
						window.history.replaceState({}, '', `${productLinkEl.dataset.productLink}`)
					})
			}

			renderFromCache(productLinkEl, productInfo) {
				this.productInfoEl.replaceWith(productInfo)
				this.productInfoEl = this.querySelector('#ProductInfo')
				l_blockPosition(this.productInfoEl)
				l_debounce(() => {
					window.history.replaceState({}, '', `${productLinkEl.dataset.productLink}`)
				}, 200)()
			}
		}
	)

	// Run once on initial page load
	if (document.readyState === 'loading') {
	  document.addEventListener('DOMContentLoaded', function () {
		initFloatingAtc();
		backToCollectionUrl();

	  });
    } else {
      initFloatingAtc();
	  backToCollectionUrl();
    }
}

if (!customElements.get('l-product-delivery-estimation')) {
	customElements.define(
		'l-product-delivery-estimation',
		class LTADeliveryEstimation extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				this.minDeliveryDateEl = this.querySelector('[data-min-delivery-text]')
				this.maxDeliveryDateEl = this.querySelector('[data-max-delivery-text]')

				this.minDays = parseInt(this.dataset.minDays)
				this.maxDays = parseInt(this.dataset.maxDays)

				const today = new Date()

				if (this.minDeliveryDateEl) {
					this.minDeliveryDateEl.innerHTML = this.getNextWorkingDays(today, this.minDays)
				}

				if (this.maxDeliveryDateEl) {
					this.maxDeliveryDateEl.innerHTML = this.getNextWorkingDays(today, this.maxDays)
				}
			}

			getNextWorkingDays(date, daysToAdd) {
				let nextDay = new Date(date)
				let addedDays = 0

				while (addedDays < daysToAdd) {
					nextDay.setDate(nextDay.getDate() + 1)

					if (nextDay.getDay() !== 0 && nextDay.getDay() !== 6) {
						addedDays++
					}
				}

				return this.formatDate(nextDay)
			}

			formatDate(date) {
				const day = date.getDate()
				const month = date.toLocaleString('en-GB', { month: 'short' })

				return `${day} ${month}`
			}
		}
	)
}

if (!customElements.get('l-product-subscriptions')) {
	customElements.define(
		'l-product-subscriptions',
		class LTAProductSubscriptions extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				// Elements
				this.subscriptionOptionEls = this.querySelectorAll('[data-subscription-option]')

				this.toggleSubscriptionInputs()
			}

			toggleSubscriptionInputs() {
				this.subscriptionOptionEls.forEach((subscriptionOptionEl) => {
					subscriptionOptionEl.addEventListener('click', () => {
						const buyingOption = subscriptionOptionEl.dataset.subscriptionOption // one_time_purchase || subscriptions

						const subscriptionsInputEls = subscriptionOptionEl.querySelectorAll('input')

						subscriptionsInputEls.forEach((inputEl) => {
							buyingOption == 'one' ? inputEl.removeAttribute('disabled') : ''
						})
					})
				})
			}
		}
	)
}
/************************************************************************************

L-PRODUCT-MEDIA-GALLERY SECTION

*************************************************************************************/
if (!customElements.get('l-product-media-gallery')) {
	customElements.define(
		'l-product-media-gallery',
		class ProductMediaGallery extends HTMLElement {
			constructor() {
				super()

				this.sectionId = this.dataset.sectionId
			}

			productVariantLoaded = undefined

			connectedCallback() {
				if (window.Swiper) this.setupCarousel()
				else {
					document.addEventListener('carousel:loaded', () => {
						this.setupCarousel()
						this.setupPhotoSwipe()
					})
				}

				this.onVariantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.productGroupChange, ({ data }) => {
					this.setupPhotoSwipe()
				})
			}

			disconnectedCallback() {
				if (this.productVariantLoaded) {
					this.productVariantLoaded()
				}
			}

			setupCarousel() {
				/*** Thumbnails carousel ***/
				this.thumbnailCarousel = this.querySelector('[data-thumbnails-carousel]')
				this.thumbnailSlides = this.thumbnailCarousel.querySelectorAll('[data-media]')
				this.thumbnailNavigationNext = this.thumbnailCarousel.querySelector('[data-next]')
				this.thumbnailNavigationPrev = this.thumbnailCarousel.querySelector('[data-prev]')

				this.thumbnailSlides.forEach((swiperSlide) => swiperSlide.classList.remove('l-hidden'))

				this.thumbnailCarouselSwiper = new window.Swiper(this.thumbnailCarousel, {
					loop: false,
					slidesPerView: 'auto',
					spaceBetween: 8,
					resistanceRatio: 0.2,
					navigation: {
						nextEl: this.thumbnailNavigationNext,
						prevEl: this.thumbnailNavigationPrev,
					},
				})

				/*** ThumbMain carousel setup ***/
				this.mainCarousel = this.querySelector('[data-main-carousel]')
				this.mediaSlides = this.mainCarousel.querySelectorAll('[data-media]')
				this.navigationNext = this.mainCarousel.querySelector('[data-next]')
				this.navigationPrev = this.mainCarousel.querySelector('[data-prev]')
				this.paginationEl = this.querySelector('[data-pagination]')

				this.mediaSlides.forEach((swiperSlide) => swiperSlide.classList.remove('l-hidden'))

				this.mainCarouselSwiper = new window.Swiper(this.mainCarousel, {
					loop: false,
					slidesPerView: 'auto',
					keyboard: {
						enabled: true,
					},
					navigation: {
						nextEl: this.navigationNext,
						prevEl: this.navigationPrev,
					},
					pagination: {
						el: this.paginationEl,
						clickable: true,
					},
					thumbs: {
						swiper: this.thumbnailCarouselSwiper,
					},
				})

				/* Setup controller */
				this.mainCarouselSwiper.controller.control = this.thumbnailCarouselSwiper
				// this.thumbnailCarouselSwiper.controller.control = this.mainCarouselSwiper
			}

			setupPhotoSwipe() {
				const backEasing = {
					in: 'cubic-bezier(0.6, -0.28, 0.7, 1)',
					out: 'cubic-bezier(0.3, 0, 0.32, 1.275)',
					inOut: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
				}

				const leftArrowSVGString =
					'<svg  xmlns="http://www.w3.org/2000/svg"  width="24"  height="24"  viewBox="0 0 24 24"  fill="none"  stroke="currentColor"  stroke-width="2"  stroke-linecap="round"  stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M15 6l-6 6l6 6" /></svg>'
				const rightArrowSVGString =
					'<svg  xmlns="http://www.w3.org/2000/svg"  width="24"  height="24"  viewBox="0 0 24 24"  fill="none"  stroke="currentColor"  stroke-width="2"  stroke-linecap="round"  stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M9 6l6 6l-6 6" /></svg>'
				const closeSVGString =
					'<svg  xmlns="http://www.w3.org/2000/svg"  width="24"  height="24"  viewBox="0 0 24 24"  fill="none"  stroke="currentColor"  stroke-width="2"  stroke-linecap="round"  stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M18 6l-12 12" /><path d="M6 6l12 12" /></svg>'

				const lightbox = new PhotoSwipeLightbox({
					gallery: '#LMainProductMedia',
					children: 'a',
					arrowPrevSVG: leftArrowSVGString,
					arrowNextSVG: rightArrowSVGString,
					closeSVG: closeSVGString,
					showHideAnimationType: 'zoom',
					showAnimationDuration: 600,
					hideAnimationDuration: 600,
					preloaderDelay: 0,
					preload: [1, 2],
					bgOpacity: 1,
					pswpModule: PhotoSwipe,
					spacing: 0.5,
					loop: false,
				})

				lightbox.on('firstUpdate', () => {
					lightbox.pswp.options.easing = backEasing.out
				})
				lightbox.on('initialZoomInEnd', () => {
					lightbox.pswp.options.easing = backEasing.inOut
				})
				lightbox.on('close', () => {
					lightbox.pswp.options.easing = backEasing.in
				})
				lightbox.init()
			}

			getVariantMediaData() {
				return JSON.parse(this.querySelector('.js-product-variants').textContent) || null
			}

			onVariantChange(data) {
				this.variantData = this.variantData || this.getVariantMediaData()
				const selectedVariantId = data.variant.id

				const matchMediaId = this.variantData.find((variant) => variant.id == selectedVariantId)?.featured_media?.id

				this.mediaSlides.forEach((mediaSlide, index0) => {
					const mediaId = mediaSlide.dataset.media

					if (mediaId == matchMediaId) {
						this.mainCarouselSwiper.slideTo(index0, true)
					}
				})
			}
		}
	)
}

/************************************************************************************

COMPONENT: PRODUCT VARIANT DESCRIPTIONS

*************************************************************************************/
if (!customElements.get('variant-descriptions')) {
	customElements.define(
		'variant-descriptions',
		class ProductVariantDescriptions extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				this.productDescEls = this.querySelectorAll('[data-product]')

				this.onVariantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.productGroupChange, ({ data }) => {
					const { productId } = data

					this.productDescEls.forEach((productDescEl) => {
						productDescEl.classList.toggle('hidden', productDescEl.dataset.product != productId)
					})
				})
			}
		}
	)
}

/************************************************************************************

COMPONENT: PRODUCT VARIANT PDPV2

*************************************************************************************/
class ProductGroup extends HTMLElement {
	connectedCallback() {
		this.productDescEls = this.querySelectorAll('[data-product]')

		this.onVariantChangeUnsubscriber = subscribe(
			PUB_SUB_EVENTS.productGroupChange,
			({ data }) => {
				const { productId } = data

				this.productDescEls.forEach((productDescEl) => {


					productDescEl.classList.toggle(
						'l-hidden',
						productDescEl.dataset.product != productId
					)
				})
			}
		)
	}

	disconnectedCallback() {
		if (this.onVariantChangeUnsubscriber) {
			this.onVariantChangeUnsubscriber()
		}
	}
}

if (!customElements.get('l-product-lenses')) {
	customElements.define(
		'l-product-lenses',
		class extends ProductGroup {}
	)
}

if (!customElements.get('l-product-fitspecs')) {
	customElements.define(
		'l-product-fitspecs',
		class extends ProductGroup {}
	)
}

if (!customElements.get('l-product-technologies')) {
	customElements.define(
		'l-product-technologies',
		class extends ProductGroup {}
	)
}

if (!customElements.get('l-product-modaltitle')) {
	customElements.define(
		'l-product-modaltitle',
		class extends ProductGroup {}
	)
}

// if (!customElements.get('l-product-lenses')) {
// 	customElements.define(
// 		'l-product-lenses',
// 		class ProductLenses extends HTMLElement {
// 			constructor() {
// 				super()
// 			}

// 			connectedCallback() {
// 				// Elements
// 				this.productDescEls = this.querySelectorAll('[data-product]')

// 				// Setup events
// 				this.onVariantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.productGroupChange, ({ data }) => {
// 					const { productId } = data

// 					this.productDescEls.forEach((productDescEl) => {
// 						productDescEl.classList.toggle('l-hidden', productDescEl.dataset.product != productId)
// 					})
// 				})
// 			}
// 		}
// 	)
// }

// if (!customElements.get('l-product-fitspecs')) {
// 	customElements.define(
// 		'l-product-fitspecs',
// 		class ProductLenses extends HTMLElement {
// 			constructor() {
// 				super()
// 			}

// 			connectedCallback() {
// 				// Elements
// 				this.productDescEls = this.querySelectorAll('[data-product]')

// 				// Setup events
// 				this.onVariantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.productGroupChange, ({ data }) => {
// 					const { productId } = data

// 					this.productDescEls.forEach((productDescEl) => {
// 						productDescEl.classList.toggle('l-hidden', productDescEl.dataset.product != productId)
// 					})
// 				})
// 			}
// 		}
// 	)
// }

// if (!customElements.get('l-product-technologies')) {
// 	customElements.define(
// 		'l-product-technologies',
// 		class ProductTechnologies extends HTMLElement {
// 			constructor() {
// 				super()
// 			}
// 			connectedCallback() {
// 				this.productDescEls = this.querySelectorAll('[data-product]')

// 				this.onVariantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.productGroupChange, ({ data }) => {
// 					const { productId } = data

// 					this.productDescEls.forEach((productDescEl) => {
// 						productDescEl.classList.toggle('l-hidden', productDescEl.dataset.product != productId)
// 					})
// 				})
// 			}
// 		}
// 	)
// }

if (!customElements.get('l-product-frame-technologies')) {
	customElements.define(
		'l-product-frame-technologies',
		class ProductFrameTechnologies extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				// Data
				this.defaultProductId = this.dataset.currentProductId
				this.productsData = JSON.parse(this.querySelector('[data-product-group]').textContent)

				// Elements
				this.productDescEls = this.querySelectorAll('[data-product]')

				// Hide if no data
				if (!this.productsData[this.defaultProductId]) {
					this.parentElement.classList.add('l-hidden')
				}

				// Catch variant picker options changed events
				this.onVariantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.productGroupChange, ({ data }) => {
					const { productId } = data

					this.parentElement.classList.toggle('l-hidden', !this.productsData[productId])

					this.productDescEls.forEach((productDescEl) => {
						productDescEl.classList.toggle('l-hidden', productDescEl.dataset.product != productId)
					})
				})
			}
		}
	)
}

